import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import { Avatar } from "./Avatar";

interface MentionTextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange"> {
  value: string;
  onChange: (value: string) => void;
  /** Board whose members are mentionable. */
  boardId: string;
}

/**
 * A textarea with @mention autocomplete.
 *
 * This isn't a nicety. An @name the backend can't resolve is silently ignored —
 * no error, no notification — so without a picker a typo just vanishes, and
 * nobody discovers that service users (`@claude`) are mentionable at all. The
 * list is the board's members, which is exactly the set the backend will accept.
 */
export function MentionTextarea({
  value,
  onChange,
  boardId,
  onKeyDown,
  ...rest
}: MentionTextareaProps) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const [query, setQuery] = useState<string | null>(null); // null = closed
  const [at, setAt] = useState(0); // index of the triggering "@"
  const [active, setActive] = useState(0);

  const { data: members = [] } = useQuery({
    queryKey: ["board-members", boardId],
    queryFn: () => api.members.list(boardId),
    enabled: query !== null, // don't fetch until someone actually types "@"
    staleTime: 60_000,
  });

  const matches = useMemo(() => {
    if (query === null) return [];
    const q = query.toLowerCase();
    return members
      .filter(
        (m) =>
          m.username.toLowerCase().startsWith(q) ||
          (m.displayName ?? "").toLowerCase().includes(q),
      )
      .slice(0, 6);
  }, [members, query]);

  useEffect(() => setActive(0), [query]);

  const open = query !== null && matches.length > 0;

  /**
   * Find an @-token immediately behind the caret. Only trigger at a word
   * boundary, so an email address (`bob@example.com`) doesn't open the menu.
   */
  function sync(el: HTMLTextAreaElement) {
    const caret = el.selectionStart;
    const upto = el.value.slice(0, caret);
    const m = /(?:^|[\s(])@([A-Za-z0-9_.-]*)$/.exec(upto);
    if (!m) {
      setQuery(null);
      return;
    }
    setAt(caret - m[1].length - 1);
    setQuery(m[1]);
  }

  function insert(username: string) {
    const el = ref.current;
    if (!el) return;
    const caret = el.selectionStart;
    const next = `${value.slice(0, at)}@${username} ${value.slice(caret)}`;
    onChange(next);
    setQuery(null);
    // restore the caret after React re-renders with the new value
    const pos = at + username.length + 2;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  }

  /**
   * Trello-style code fence: pressing Enter right after typing an opening
   * ```` ``` ```` (optionally with a language) drops in the closing fence and
   * parks the caret on the blank line between them. Returns true if it fired.
   */
  function maybeCloseFence(el: HTMLTextAreaElement): boolean {
    const caret = el.selectionStart;
    if (caret !== el.selectionEnd) return false; // don't fight a selection
    const before = value.slice(0, caret);
    const line = before.slice(before.lastIndexOf("\n") + 1);
    if (!/^```[A-Za-z0-9-]*$/.test(line)) return false;
    // Already an unclosed fence below? leave it to the plain newline.
    if (value.slice(caret).includes("```")) return false;
    const next = `${before}\n\n\`\`\`${value.slice(caret)}`;
    onChange(next);
    const pos = caret + 1; // the blank middle line
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(pos, pos);
    });
    return true;
  }

  return (
    <div className="relative">
      <textarea
        {...rest}
        ref={ref}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          sync(e.target);
        }}
        onClick={(e) => sync(e.currentTarget)}
        onBlur={() => setQuery(null)}
        onKeyDown={(e) => {
          if (open) {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActive((i) => (i + 1) % matches.length);
              return;
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive((i) => (i - 1 + matches.length) % matches.length);
              return;
            }
            if (e.key === "Enter" || e.key === "Tab") {
              e.preventDefault();
              insert(matches[active].username);
              return;
            }
            if (e.key === "Escape") {
              // Close the menu only — don't let a surrounding Modal see this and
              // shut the whole card.
              e.preventDefault();
              e.stopPropagation();
              setQuery(null);
              return;
            }
          }
          if (
            e.key === "Enter" &&
            !e.metaKey &&
            !e.ctrlKey &&
            !e.altKey &&
            !e.shiftKey &&
            maybeCloseFence(e.currentTarget)
          ) {
            e.preventDefault();
            return;
          }
          onKeyDown?.(e);
        }}
      />

      {open && (
        <ul
          role="listbox"
          aria-label="Mention a member"
          className="absolute bottom-full z-20 mb-1 w-64 overflow-hidden rounded-lg border border-slate-200 bg-[var(--surface)] py-1 shadow-lg"
        >
          {matches.map((m, i) => (
            <li key={m.userId}>
              <button
                type="button"
                role="option"
                aria-selected={i === active}
                // mousedown, not click: blur would close the menu first.
                onMouseDown={(e) => {
                  e.preventDefault();
                  insert(m.username);
                }}
                onMouseEnter={() => setActive(i)}
                className={`flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm ${
                  i === active ? "bg-blue-50" : ""
                }`}
              >
                <Avatar userId={m.userId} name={m.displayName || m.username} />
                <span className="min-w-0 flex-1 truncate">
                  <span className="font-medium text-slate-800">
                    {m.displayName || m.username}
                  </span>{" "}
                  <span className="text-slate-500">@{m.username}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
